import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { useSession } from "@/hooks/useSession";
import { guestApi, isTransportError, USE_FIXTURES } from "@/lib/api";

export const VerifyOtp = () => {
  const [params] = useSearchParams();
  const email = params.get("email") || "your@email.com";
  const navigate = useNavigate();
  const { login } = useSession();

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [seconds, setSeconds] = useState(45);
  const inputs = useRef([]);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const onChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) {
      toast.error("Enter all 6 digits");
      return;
    }
    setSubmitting(true);
    try {
      const res = await guestApi.verifyEmail({ email, otp: code });
      const profile = res?.unwrapped?.customer || { email, name: email.split("@")[0] };
      login({
        id: profile.id,
        email: profile.email || email,
        name: profile.name || "Guest",
        phone: profile.phone,
        emailVerified: true,
        email_verified_at: profile.email_verified_at,
        email_notifications_enabled: profile.email_notifications_enabled,
        email_notify_booking_chat: profile.email_notify_booking_chat,
      });
      toast.success(res?.message || "Verified. Welcome to StayEasy.");
      navigate("/account");
    } catch (err) {
      if (USE_FIXTURES && isTransportError(err)) {
        login({ email, name: "Alex Morel", emailVerified: true });
        toast.success("Verified (fixture mode).");
        navigate("/account");
        return;
      }
      toast.error(
        err?.response?.data?.message ||
          err?.apiMessage ||
          err?.message ||
          "Invalid or expired code.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setSeconds(45);
    try {
      const res = await guestApi.resendVerification({ email });
      toast.success(res?.message || `New code sent to ${email}`);
    } catch (err) {
      if (USE_FIXTURES && isTransportError(err)) {
        toast.success("Fixture mode: use OTP 111111.");
      } else {
        toast.error(
          err?.response?.data?.message ||
            err?.apiMessage ||
            err?.message ||
            "Could not resend code.",
        );
      }
    }
  };

  return (
    <AuthShell
      eyebrow="Verify email · OTP"
      title={<>Check your<br /><span className="italic">inbox</span>.</>}
      subtitle={<>We sent a 6-digit code to <strong className="text-foreground">{email}</strong>. It expires in 10 minutes.</>}
      image="https://images.unsplash.com/photo-1710563138874-4bac91c14e51?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85"
      imageCaption="Palm Bungalow 04 · Nusa Lembongan"
    >
      <form onSubmit={onSubmit} className="space-y-8" data-testid="otp-form">
        <div className="flex justify-between gap-2 md:gap-3">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              data-testid={`otp-digit-${i}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => onChange(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className="h-14 w-full border border-foreground/20 bg-transparent text-center font-display text-2xl focus:border-foreground focus:outline-none md:h-16"
            />
          ))}
        </div>

        <button
          type="submit"
          data-testid="otp-submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Verify email"} <ArrowRight size={16} />
        </button>

        <p className="text-center text-sm text-muted-foreground">
          Didn't get it?{" "}
          {seconds > 0 ? (
            <span className="text-foreground/60">Resend in {seconds}s</span>
          ) : (
            <button
              type="button"
              onClick={resend}
              className="font-medium text-foreground underline"
            >
              Resend code
            </button>
          )}
        </p>

        <p className="text-center text-xs text-muted-foreground">
          Wrong address?{" "}
          <Link to="/register" className="underline">Change email</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default VerifyOtp;
